import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, Col, Row, Container, Spinner, ProgressBar } from "react-bootstrap";
import DepartmentTopbar from "./components/DepartmentTopbar";
import DepartmentSidebar from "./components/DepertmentSidebar";
import styles from "../../assets/css/Dashboard.module.css";
import {
  FaUsers,
  FaMoneyBillWave,
  FaCheckCircle,
  FaTimesCircle,
  FaChartLine,
  FaWater,
  FaToilet
} from "react-icons/fa";

// Reusable Stat Card Component for HFA
const StatCard = ({ title, value, icon, color, subValue, subTitle }) => (
  <Col xl={3} md={6} className="mb-4">
    <Card className="h-100 border-0 shadow-sm">
      <Card.Body>
        <Row className="align-items-center">
          <Col xs={8}>
            <h6 className="text-uppercase text-muted mb-1 small">{title}</h6>
            <h3 className="mb-0">{value}</h3>
            {subValue && (
              <p className="text-muted mb-0 small">
                {subTitle}: {subValue}
              </p>
            )}
          </Col>
          <Col xs={4} className="text-end">
            <div
              className="rounded-circle p-3 d-inline-flex align-items-center justify-content-center"
              style={{ backgroundColor: `${color}15`, color: color }}
            >
              {icon}
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  </Col>
);

// Installment Progress Card
const InstallmentCard = ({ installment, data }) => {
  const total = (data?.paid || 0) + (data?.unpaid || 0);
  const percentage = total > 0 ? Math.round((data?.paid / total) * 100) : 0;

  return (
    <Col xl={4} md={6} className="mb-4">
      <Card className="h-100 border-0 shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">{installment.replace('_', ' ')}</h6>
            <span className="badge bg-primary">{percentage}%</span>
          </div>
          <ProgressBar now={percentage} className="mb-2" style={{ height: '10px' }} />
          <div className="d-flex justify-content-between small text-muted">
            <span>Paid: {data?.paid || 0}</span>
            <span>Pending: {data?.unpaid || 0}</span>
          </div>
        </Card.Body>
      </Card>
    </Col>
  );
};


function DepartmentDashboard() {
  const navigate = useNavigate();
  const [pwdReportSummary, setPwdReportSummary] = useState({
    total: 0,
    pending: 0,
    complete: 0,
    loading: true,
    error: null,
  });

  const [departmentType, setDepartmentType] = useState("");

  // Get user information for filtering
  const user_id = localStorage.getItem("user_id");
  const userRole = localStorage.getItem("role");
  const isSuperAdmin = userRole === "Super_Admin";

  // Filtering functions (same as in pwdreportlist.js)
  const matchesLoggedInUser = (item) => {
    const concernedId =
      item?.name_of_concerned_id ?? item?.name_of_concerned ?? "";
    return String(concernedId) === String(user_id ?? "");
  };

  const filterDataForUser = (data) => {
    if (!Array.isArray(data)) return [];
    if (isSuperAdmin || localStorage.getItem("type") === 'Accountant' || localStorage.getItem("type") === 'AE' || localStorage.getItem("type") === 'F.O' || localStorage.getItem("type") === 'E.O' || localStorage.getItem("type") === 'MCIC') return data;

    // For SAE/AE users: show reports where they are the concerned user OR where they are the receiver
    return data.filter(item => {
      const concernedId = item?.name_of_concerned_id || item?.name_of_concerned || '';
      const receiverId = item?.receiver_id || '';

      // Show if user is the concerned person OR if user is the receiver
      return String(concernedId) === String(user_id || '') ||
        String(receiverId) === String(user_id || '');
    });
  };

  const [hfaStats, setHfaStats] = useState({
    total_beneficiaries: 0,
    user_beneficiaries: 0,
    installments: {},
    loading: true,
  });


  useEffect(() => {
    const type = localStorage.getItem("type");
    const token = localStorage.getItem("token");
    setDepartmentType(type);
    const fetchHfaData = async () => {
      try {
        // In a real app, you would make an actual API call here
        const response = await fetch(`${process.env.REACT_APP_SUPER_ADMIN_API_URL}hfa-dashboard.php`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const data = await response.json();


        if (data.success) {
          setHfaStats({
            total_beneficiaries: data.total_beneficiaries,
            user_beneficiaries: data.user_beneficiaries,
            installments: data.installments,
            loading: false
          });
        }
      } catch (error) {
        console.error("Error fetching HFA data:", error);
        setHfaStats(prev => ({ ...prev, loading: false, error: "Failed to load data" }));
      }
    };

    const fetchPWDReportSummary = async () => {
      setPwdReportSummary(prev => ({ ...prev, loading: true, error: null }));
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${process.env.REACT_APP_SUPER_ADMIN_API_URL}/pwd-list.php`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        if (Array.isArray(data)) {
          // Apply user-based filtering
          const filteredData = filterDataForUser(data);

          // Debug logging to verify filtering is working
          console.log(`Dashboard: Total reports from API: ${data.length}, Filtered for user: ${filteredData.length}`);

          const total = filteredData?.length;
          const pending = filteredData?.filter(
            (item) =>
              (item.final_status || "").toLowerCase().trim() === "pending"
          )?.length;
          const complete = filteredData?.filter(
            (item) =>
              (item.final_status || "").toLowerCase().trim() === "complete"
          ).length;
          setPwdReportSummary({
            total,
            pending,
            complete,
            loading: false,
            error: null,
          });
        } else {
          setPwdReportSummary({ total: 0, pending: 0, complete: 0, loading: false, error: "Invalid data" });
        }
      } catch (error) {
        setPwdReportSummary({ total: 0, pending: 0, complete: 0, loading: false, error: error.message });
      }
    };

    const fetchHfaDashboardData = async () => {
      setHfaStats(prev => ({ ...prev, loading: true }));
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_SUPER_ADMIN_API_URL}/hfa-dashboard.php`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data.success) {
          setHfaStats({
            total_beneficiaries: response.data.total_beneficiaries,
            installments: response.data.installments,
            loading: false,
          });
        } else {
          setHfaStats(prev => ({ ...prev, loading: false }));
        }
      } catch (error) {
        console.error("Failed to fetch HFA dashboard data", error);
        setHfaStats(prev => ({ ...prev, loading: false }));
      }
    };

    if (type === "HFA") {
      fetchHfaData();
    } else {
      fetchPWDReportSummary();
    }
  }, [departmentType]);

  // Calculate total paid and pending across all installments
  const calculateTotals = () => {
    let totalPaid = 0;
    let totalPending = 0;

    Object.values(hfaStats.installments || {}).forEach(installment => {
      totalPaid += installment.paid || 0;
      totalPending += installment.unpaid || 0;
    });

    return { totalPaid, totalPending };
  };

  const { totalPaid, totalPending } = calculateTotals();
  const completionRate = hfaStats.total_beneficiaries > 0
    ? Math.round((totalPaid / (totalPaid + totalPending)) * 100)
    : 0;

  const sanitaryMenuItems = [
    {
      name: "Water Tank",
      icon: <FaWater />,
      path: "/departmentDashboard/sanitary",
    },
    {
      name: "Sanitary",
      icon: <FaToilet />,
      path: "/departmentDashboard/water-tank",
    },
  ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  const renderDashboardContent = () => {
    if (departmentType === "HFA") {
      if (hfaStats.loading) {
        return (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        );
      }

      return (
        <div>

          {/* Summary Cards */}
          <Row className="mb-4">
            <StatCard
              title="Total Beneficiaries"
              value={hfaStats.total_beneficiaries.toLocaleString()}
              icon={<FaUsers size={20} />}
              color="#4e73df"
            />
            <StatCard
              title="Your Beneficiaries"
              value={hfaStats.user_beneficiaries.toLocaleString()}
              icon={<FaUsers size={20} />}
              color="#36b9cc"
            />
            <StatCard
              title="Total Installments Paid"
              value={totalPaid.toLocaleString()}
              subValue={totalPending.toLocaleString()}
              subTitle="Pending"
              icon={<FaMoneyBillWave size={20} />}
              color="#1cc88a"
            />
            <StatCard
              title="Total Unpaid Installments"
              value={totalPending.toLocaleString()}
              icon={<FaTimesCircle size={20} />}
              color="#e74a3b"
            />
          </Row>

          {/* Installments Progress */}
          <h5 className="mb-3">Installment Progress</h5>
          <Row>
            {Object.entries(hfaStats.installments || {}).map(([key, value]) => (
              <InstallmentCard
                key={key}
                installment={key.replace('_', ' ')}
                data={value}
              />
            ))}
          </Row>
        </div>
      );
    } else if (departmentType === "Sanitary") {
      return (
        <div className={styles.cardGrid}>
          {sanitaryMenuItems.map((item, index) => (
            <div
              key={index}
              className={styles.card}
              onClick={() => handleCardClick(item.path)}
            >
              <div className={styles.iconWrapper}>{item.icon}</div>
              <div className={styles.cardContent}>
                <div className={styles.cardTitle}>{item.name}</div>
              </div>
            </div>
          ))}
        </div>
      );
    } else {
      // Default PWD Dashboard
      return (
        <div className={styles.cardGrid}>
          {/* PWD Cards */}
          <div className={styles.card}>
            <div className={styles.cardContent}>
              <h3 className={styles.cardTitle}>Total PWD Reports</h3>
              <p className={styles.cardDescription}>All PWD Reports</p>
              {pwdReportSummary.loading ? <div>Loading...</div> : <div className={styles.statsValue}>{pwdReportSummary.total ?? 0}</div>}
            </div>
          </div>
          <div className={styles.card}>
            <div className={styles.cardContent}>
              <h3 className={styles.cardTitle}>Pending PWD Reports</h3>
              <p className={styles.cardDescription}>Pending Status</p>
              {pwdReportSummary.loading ? <div>Loading...</div> : <div className={styles.statsValue} style={{ color: '#f44336' }}>{pwdReportSummary.pending ?? 0}</div>}
            </div>
          </div>
          <div className={styles.card}>
            <div className={styles.cardContent}>
              <h3 className={styles.cardTitle}>Completed PWD Reports</h3>
              <p className={styles.cardDescription}>Completed Status</p>
              {pwdReportSummary.loading ? <div>Loading...</div> : <div className={styles.statsValue} style={{ color: '#4caf50' }}>{pwdReportSummary.complete ?? 0}</div>}
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <>
      <DepartmentTopbar />
      <div className="d-flex" style={{ minHeight: "calc(100vh - 56px)" }}>
        <DepartmentSidebar />
        <div className="flex-grow-1 p-4 bg-light">
          <div className={styles.dashboardContent}>
            <h2 className="mb-4">Department Dashboard ({departmentType})</h2>
            {renderDashboardContent()}
          </div>
        </div>
      </div>
    </>
  );
}

export default DepartmentDashboard;

